/**
 * Enhanced Error Types and Utilities for Historical Reports
 * 
 * Provides structured error handling with user-friendly messages
 * and appropriate retry strategies
 */

export enum HistoricalReportsErrorCode {
    // Network/API Errors
    NETWORK_ERROR = 'NETWORK_ERROR',
    API_TIMEOUT = 'API_TIMEOUT',
    API_SERVER_ERROR = 'API_SERVER_ERROR',
    API_NOT_FOUND = 'API_NOT_FOUND',

    // Authentication Errors
    AUTH_REQUIRED = 'AUTH_REQUIRED',
    AUTH_INVALID = 'AUTH_INVALID',
    AUTH_EXPIRED = 'AUTH_EXPIRED',

    // Data Errors
    INVALID_RESPONSE = 'INVALID_RESPONSE',
    REPORT_NOT_FOUND = 'REPORT_NOT_FOUND',

    // Client Errors
    INVALID_REQUEST = 'INVALID_REQUEST',
    RATE_LIMITED = 'RATE_LIMITED',

    // Unknown
    UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

export interface HistoricalReportsError {
    code: HistoricalReportsErrorCode;
    message: string;
    userMessage: string;
    retryable: boolean;
    details?: any;
    timestamp: string;
}

/**
 * Creates a structured error from various error sources
 */
export function createHistoricalReportsError(
    error: unknown,
    context?: string
): HistoricalReportsError {
    const timestamp = new Date().toISOString();

    // Handle native Error objects
    if (error instanceof Error) {
        // Check for specific error patterns
        if (error.message.includes('fetch')) {
            return {
                code: HistoricalReportsErrorCode.NETWORK_ERROR,
                message: error.message,
                userMessage: 'Unable to connect to the server. Please check your internet connection and try again.',
                retryable: true,
                timestamp,
            };
        }

        if (error.message.includes('timeout')) {
            return {
                code: HistoricalReportsErrorCode.API_TIMEOUT,
                message: error.message,
                userMessage: 'The request took too long to complete. Please try again.',
                retryable: true,
                timestamp,
            };
        }

        if (error.message.includes('401') || error.message.includes('unauthorized')) {
            return {
                code: HistoricalReportsErrorCode.AUTH_INVALID,
                message: error.message,
                userMessage: 'Authentication failed. Please log in again.',
                retryable: false,
                timestamp,
            };
        }

        if (error.message.includes('403') || error.message.includes('forbidden')) {
            return {
                code: HistoricalReportsErrorCode.AUTH_INVALID,
                message: error.message,
                userMessage: 'You do not have permission to access this resource.',
                retryable: false,
                timestamp,
            };
        }

        if (error.message.includes('404') || error.message.includes('not found')) {
            return {
                code: HistoricalReportsErrorCode.REPORT_NOT_FOUND,
                message: error.message,
                userMessage: 'The requested report could not be found.',
                retryable: false,
                timestamp,
            };
        }

        if (error.message.includes('500') || error.message.includes('server error')) {
            return {
                code: HistoricalReportsErrorCode.API_SERVER_ERROR,
                message: error.message,
                userMessage: 'A server error occurred. Please try again in a few moments.',
                retryable: true,
                timestamp,
            };
        }

        if (error.message.includes('429') || error.message.includes('rate limit')) {
            return {
                code: HistoricalReportsErrorCode.RATE_LIMITED,
                message: error.message,
                userMessage: 'Too many requests. Please wait a moment before trying again.',
                retryable: true,
                timestamp,
            };
        }
    }

    // Handle authentication-specific errors
    if (typeof error === 'string') {
        if (error.includes('Not authenticated') || error.includes('user email required')) {
            return {
                code: HistoricalReportsErrorCode.AUTH_REQUIRED,
                message: error,
                userMessage: 'Please log in to view reports.',
                retryable: false,
                timestamp,
            };
        }
    }

    // Default fallback
    const message = error instanceof Error ? error.message : String(error);
    return {
        code: HistoricalReportsErrorCode.UNKNOWN_ERROR,
        message: `${context ? `${context}: ` : ''}${message}`,
        userMessage: 'An unexpected error occurred. Please try again or contact support if the problem persists.',
        retryable: true,
        details: error,
        timestamp,
    };
}

/**
 * Enhanced retry logic with exponential backoff and jitter
 */
export async function retryWithBackoff<T>(
    operation: () => Promise<T>,
    maxAttempts: number = 3,
    baseDelay: number = 1000,
    maxDelay: number = 10000,
    context?: string
): Promise<T> {
    let lastError: unknown;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            return await operation();
        } catch (error) {
            lastError = error;

            const structuredError = createHistoricalReportsError(error, context);

            // Don't retry if error is not retryable
            if (!structuredError.retryable || attempt === maxAttempts) {
                throw structuredError;
            }

            // Calculate delay with exponential backoff and jitter
            const exponentialDelay = Math.min(baseDelay * Math.pow(2, attempt - 1), maxDelay);
            const jitter = Math.random() * 0.1 * exponentialDelay; // ±10% jitter
            const delay = exponentialDelay + jitter;

            console.warn(`Attempt ${attempt}/${maxAttempts} failed, retrying in ${Math.round(delay)}ms:`, structuredError);

            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }

    // Should never reach here, but TypeScript requires it
    throw createHistoricalReportsError(lastError, context);
}

/**
 * User-friendly error messages mapping
 */
export const ERROR_MESSAGES = {
    [HistoricalReportsErrorCode.NETWORK_ERROR]: {
        title: 'Connection Problem',
        message: 'Unable to connect to the server. Please check your internet connection and try again.',
        action: 'Try Again',
    },
    [HistoricalReportsErrorCode.API_TIMEOUT]: {
        title: 'Request Timeout',
        message: 'The request took too long to complete. This might be due to high server load.',
        action: 'Try Again',
    },
    [HistoricalReportsErrorCode.API_SERVER_ERROR]: {
        title: 'Server Error',
        message: 'A server error occurred. Our team has been notified and is working on a fix.',
        action: 'Try Again Later',
    },
    [HistoricalReportsErrorCode.API_NOT_FOUND]: {
        title: 'Not Found',
        message: 'The requested resource could not be found.',
        action: 'Go Back',
    },
    [HistoricalReportsErrorCode.AUTH_REQUIRED]: {
        title: 'Authentication Required',
        message: 'Please log in to access reports.',
        action: 'Log In',
    },
    [HistoricalReportsErrorCode.AUTH_INVALID]: {
        title: 'Authentication Failed',
        message: 'Your session has expired or is invalid. Please log in again.',
        action: 'Log In',
    },
    [HistoricalReportsErrorCode.AUTH_EXPIRED]: {
        title: 'Session Expired',
        message: 'Your session has expired. Please log in again to continue.',
        action: 'Log In',
    },
    [HistoricalReportsErrorCode.INVALID_RESPONSE]: {
        title: 'Invalid Response',
        message: 'Received an unexpected response from the server. Please try again.',
        action: 'Try Again',
    },
    [HistoricalReportsErrorCode.REPORT_NOT_FOUND]: {
        title: 'Report Not Found',
        message: 'The requested report could not be found. It may have been deleted or moved.',
        action: 'Go Back',
    },
    [HistoricalReportsErrorCode.INVALID_REQUEST]: {
        title: 'Invalid Request',
        message: 'The request contains invalid data. Please check your input and try again.',
        action: 'Fix Input',
    },
    [HistoricalReportsErrorCode.RATE_LIMITED]: {
        title: 'Too Many Requests',
        message: 'You are making requests too quickly. Please wait a moment before trying again.',
        action: 'Wait and Try Again',
    },
    [HistoricalReportsErrorCode.UNKNOWN_ERROR]: {
        title: 'Unexpected Error',
        message: 'An unexpected error occurred. If this continues, please contact support.',
        action: 'Try Again',
    },
} as const;

/**
 * Error logging utility
 */
export function logHistoricalReportsError(error: HistoricalReportsError, context?: Record<string, any>) {
    const logData = {
        timestamp: error.timestamp,
        code: error.code,
        message: error.message,
        retryable: error.retryable,
        context,
        userAgent: navigator.userAgent,
        url: window.location.href,
    };

    // In development, log to console
    if (process.env.NODE_ENV === 'development') {
        console.error('Historical Reports Error:', logData);
    }

    // In production, you might want to send to error tracking service
    // Example: Sentry, LogRocket, etc.
    // trackError('historical-reports-error', logData);
}